import {
  CellComponentProperties,
  CellComponentProperty,
  ColumnDefinition,
  CURRENT_ROW_OBJECT,
  CURRENT_SELECTORS_CONTAINER,
  LoadableSelectorContainer
} from '@aviary/components/table/table.component.types';
import {MxSelector, MxSelectorCreator, MxSelectorCreatorNameMatchingWithReturnType} from '@aviary/store/store.types';
import {camelCaseToTitleCase} from '@aviary/store/transformation.helpers';
import {isArray, isPopulatedString, isString, isUndefined} from '@aviary/store/common/typing.helpers';

export const GENERATED_COLUMN_NAME_PREFIX = 'GENERATED_COLUMN_NAME';
export const MISSING_HEADER_PREFIX = 'MISSING_HEADER';

/** Resolves a selector to its current value.  The live table resolves reactively via SelectPipe; the printable view resolves a one-time snapshot. */
export type SelectorValueResolver = <T>(selector: MxSelector<T>) => T | undefined;

/** Resolves a definition property that may be a selector creator, or the name of a selector creator within `container`, to the selector creator itself. */
export function getSelectorCreatorFrom<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer, T>(
  container: DefaultSelectorContainer,
  definitionPropertyValue?: string | MxSelectorCreatorNameMatchingWithReturnType<ObjectType, DefaultSelectorContainer, T> | MxSelectorCreator<[ObjectType], T>
): MxSelectorCreator<[ObjectType], T> | undefined {
  if (isUndefined(definitionPropertyValue)) return undefined;

  return isString(definitionPropertyValue)
    ? container[String(definitionPropertyValue)]
    : definitionPropertyValue;
}

export function generateColumnNameFrom<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer>(
  columnDefinition: ColumnDefinition<ObjectType, DefaultSelectorContainer>, columnIndex: number
): string {
  if (columnDefinition.type === 'component') return columnDefinition.columnName;
  if ((columnDefinition.type === 'text' || columnDefinition.type === 'statusChip') && isPopulatedString(columnDefinition.overrideColumnName)) {
    return columnDefinition.overrideColumnName;
  }
  if (isString(columnDefinition.value)) return columnDefinition.value.replace(/For$/, '');

  return `${GENERATED_COLUMN_NAME_PREFIX}_${columnIndex}`;
}

export function generateColumnHeaderFrom<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer>(
  columnDefinition: ColumnDefinition<ObjectType, DefaultSelectorContainer>, columnName: string, columnIndex: number
): string {
  if (columnDefinition.header) return columnDefinition.header;

  return columnName.startsWith(GENERATED_COLUMN_NAME_PREFIX)
    ? `${MISSING_HEADER_PREFIX}_${columnIndex}`
    : camelCaseToTitleCase(columnName);
}

/** Resolves one entry of a component column's `componentProperties` to the plain value that should be assigned to the cell component's input. */
export function resolveCellComponentPropertyValue<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer>(
  definitionPropertyValue: CellComponentProperty<ObjectType, DefaultSelectorContainer>,
  rowObject: ObjectType,
  container: DefaultSelectorContainer,
  resolveSelector: SelectorValueResolver
): unknown {
  switch (definitionPropertyValue) {
    case true:
      return true;
    case false:
      return false;
    case CURRENT_ROW_OBJECT:
      return rowObject;
    case CURRENT_SELECTORS_CONTAINER:
      return container;
  }

  if (isArray(definitionPropertyValue)) return definitionPropertyValue;

  const selectorCreator = getSelectorCreatorFrom(container, definitionPropertyValue);
  return selectorCreator ? resolveSelector(selectorCreator(rowObject)) : definitionPropertyValue;
}

/** Resolves a component column's `componentProperties` to a plain `{inputName: value}` object. */
export function resolveCellComponentProperties<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer>(
  properties: CellComponentProperties<ObjectType, DefaultSelectorContainer>,
  rowObject: ObjectType,
  container: DefaultSelectorContainer,
  resolveSelector: SelectorValueResolver
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([propertyName, definitionPropertyValue]) =>
      [propertyName, resolveCellComponentPropertyValue(definitionPropertyValue, rowObject, container, resolveSelector)]
    )
  );
}
